<template>
  <div>
    <div class="row beast-member-add" v-if="canEdit">
      <div class="alert col-12" :class="`alert-${alertStatus}`" v-if="alertMessage" v-text="alertMessage"></div>
      <div class="col-8">
        <div class="form-group">
          <label>Package Name</label>
          <input type="text" class="form-control" v-model="pkgInfo.packageName"/>
        </div>
      </div>
      <div class="col-2">
        <div class="form-group text-center">
          <label>Read Only</label>
          <font-awesome-icon
            class="beast-readonly-toggle"
            :icon="readOnlyIcon"
            @click="toggleAdd"/>
        </div>
      </div>
      <div class="col-2">
        <div class="form-group text-center">
          <label>&nbsp;</label>
          <div class="btn-group btn-block">
            <button class="btn btn-success" @click="storeData">
              <font-awesome-icon :icon="faPlus"/>
            </button>
            <button class="btn btn-danger" @click="clearData">
              <font-awesome-icon :icon="faTimes"/>
            </button>
          </div>
        </div>
      </div>
    </div>
    <table class="table table-striped table-borderless beast-table">
      <thead>
      <tr>
        <th class="beast-table-name">Name</th>
        <th class="beast-table-readOnly" v-if="canEdit">Read Only</th>
        <th class="beast-table-edit" v-if="canEdit">Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr v-bind:key="pkg.name"
          v-for="pkg in packages.data">
        <td class="beast-table-name">
          <router-link :to="`/package/${pkg.name}`">
            {{pkg.name}}
          </router-link>
        </td>
        <td class="beast-table-readOnly">
          <font-awesome-icon :icon="pkg.readOnly ? faToggleOn : faToggleOff" @click="toggle(pkg)"/>
        </td>
        <td class="beast-table-edit" v-if="canEdit">
          <button class="btn btn-danger" @click="remove(pkg.name)">
            <font-awesome-icon :icon="faTimes"/>
          </button>
        </td>
      </tr>
      </tbody>
    </table>

    <pagination :current="packages.page"
                :pages="packages.pages"
                @pageChange="readData"/>
  </div>
</template>


<style scoped lang="scss">
  .beast-readonly-toggle {
    display: block;
    margin: auto;
    vertical-align: middle;
    cursor: pointer;
    font-size: 17pt;

    &.fa-toggle-on {
      color: $beast-green;
    }
  }
  .beast-table {
    .beast-table-name {
      width: 60%;
      vertical-align: middle;
      a {
        color: $beast-dark-gray;

        &:hover {
          color: $beast-green
        }
      }
    }
    .beast-table-readOnly {
      width: 20%;
      vertical-align: middle;
      text-align: center;
    }
    td.beast-table-readOnly{
      cursor: pointer;
      font-size: 17pt;

      .fa-toggle-on {
        color: $beast-green;
      }
    }
    .beast-table-edit {
      width:10%;
      vertical-align: middle;
      text-align: center;

      .btn {
        margin-right: 5px;
      }
    }
  }

  .beast-member-add {
    label {
      font-weight: bold;
    }
  }
</style>

<script>
import { faTimes, faPlus, faToggleOff, faToggleOn } from "@fortawesome/free-solid-svg-icons";

import Pagination from "../../components/utils/Pagination.vue";

export default {
  components: { Pagination },
  props: {
    org: Object,
    teamId: String
  },
  watch: {
    async org() {
      await this.readData();
    }
  },
  computed: {
    apiURL() {
      return `/organizations/${this.org.slug}/teams/${this.teamId}/packages`;
    },
    canEdit() {
      return this.isMember && this.org.allowedToEdit;
    },
    isMember() {
      return this.$auth.check() && this.org && this.org.isMember;
    },
    faTimes() {
      return faTimes;
    },
    faPlus() {
      return faPlus;
    },
    faToggleOff() {
      return faToggleOff;
    },
    faToggleOn() {
      return faToggleOn;
    },
    readOnlyIcon() {
      return this.pkgInfo.readOnly
        ? faToggleOn
        : faToggleOff;
    }
  },
  data() {
    return {
      alertStatus: "success",
      alertMessage: "",
      packages: {
        data: [],
        count: 0,
        page: 0,
        pages: 0,
        size: 0
      },
      pkgInfo: {
        packageName: "",
        readOnly: false
      }
    };
  },
  methods: {
    toggleAdd() {
      this.pkgInfo.readOnly = !this.pkgInfo.readOnly;
    },
    async storeData() {
      try {
        await this.axios.post(this.apiURL, this.pkgInfo);
        this.clearData();
        await this.readData();
      } catch (e) {
        this.alertStatus = "danger";
        this.alertMessage = this._extractErrors(e);
      } finally {
        setTimeout(() => {
          this.alertMessage = "";
        }, 2500);
      }
    },
    clearData() {
      this.pkgInfo = {
        packageName: "",
        readOnly: false
      };
    },
    async readData(page = 1) {
      const queryParams = {
        params: {
          size: 10,
          page
        }
      };

      const response = await this.axios.get(this.apiURL, queryParams);
      this.packages = response.data;
    },
    async toggle(pkg) {
      try {
        const response = await this.axios.put(this.apiURL, {
          packageName: pkg.name,
          readOnly: !pkg.readOnly
        });
        pkg.readOnly = response.data.readOnly;
      } catch (e) {
        this.alertStatus = "danger";
        this.alertMessage = this._extractErrors(e);
      } finally {
        setTimeout(() => {
          this.alertMessage = "";
        }, 2500);
      }
    },
    async remove(packageName) {
      try {
        await this.$dialog.confirm("Are you sure you want to delete the package?", {
          okText: "Yes",
          cancelText: "Cancel"
        });
        await this.axios.delete(this.apiURL, {
          data: {
            packageName
          }
        });
        this.alertStatus = "success";
        this.alertMessage = "Operation was successfully executed.";
        await this.readData();
      } catch (err) {
        this.alertStatus = "danger";
        this.alertMessage = this._extractErrors(err);
      } finally {
        setTimeout(() => {
          this.alertMessage = "";
        }, 3500);
      }
    },
    _extractErrors(err) {
      if (!err.response || !err.response.data) {
        return err.message;
      }

      return err.response.data.message;
    }
  },
  async beforeMount() {
    if (this.org.slug !== "") {
      await this.readData();
    }
  }
};
</script>
